/**
 * Signature Management Page
 *
 * Main page for consultants to create and manage their signatures
 * used in retainer agreements and legal documents.
 */

import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Container, Paper, Typography, Alert, CircularProgress } from '@mui/material';
import * as S from './SignatureManagement.styled';
import { AppDispatch } from 'redux/store';
import { loadSignatures } from 'redux/signatures/signatures.middleware';
import {
    selectSignatures,
    selectSignaturesLoading,
    selectSignaturesError,
    selectCanCreateSignature,
    clearError,
} from 'redux/signatures/signatures.slice';
import TypedSignatureCreator from './components/TypedSignatureCreator';
import DrawnSignatureCreator from './components/DrawnSignatureCreator';
import UploadSignatureCreator from './components/UploadSignatureCreator';
import SignatureList from './components/SignatureList';

interface TabPanelProps {
    children?: React.ReactNode;
    tabId: string;
    activeTab: string;
}

function TabPanel(props: TabPanelProps) {
    const { children, activeTab, tabId, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={activeTab !== tabId}
            id={`signature-tabpanel-${tabId}`}
            aria-labelledby={`signature-tab-${tabId}`}
            {...other}
        >
            {activeTab === tabId && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

export default function SignatureManagement() {
    const dispatch = useDispatch<AppDispatch>();
    const signatures = useSelector(selectSignatures);
    const isLoading = useSelector(selectSignaturesLoading);
    const error = useSelector(selectSignaturesError);
    const canCreate = useSelector(selectCanCreateSignature);

    const [mainTab, setMainTab] = useState<'create' | 'my-signatures'>('create');
    const [createTab, setCreateTab] = useState<'type' | 'draw' | 'upload'>('type');

    useEffect(() => {
        dispatch(loadSignatures());
    }, [dispatch]);

    useEffect(() => {
        // Clear error after 5 seconds
        if (error) {
            const timer = setTimeout(() => {
                dispatch(clearError());
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [error, dispatch]);

    const handleMainTabChange = (newValue: 'create' | 'my-signatures') => {
        setMainTab(newValue);
    };

    const handleCreateTabChange = (newValue: 'type' | 'draw' | 'upload') => {
        setCreateTab(newValue);
    };

    const handleSignatureCreated = () => {
        // Switch to "My Signatures" tab after creating
        setMainTab('my-signatures');
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Signature Management
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Create and manage your digital signatures for retainer agreements and documents.
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => dispatch(clearError())}>
                    {error}
                </Alert>
            )}

            {!canCreate && mainTab === 'create' && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    You have reached the maximum limit of 10 signatures. Please delete an existing
                    signature to create a new one.
                </Alert>
            )}

            <Paper elevation={2}>
                <S.TabContainer>
                    <S.Tab
                        active={mainTab === 'create'}
                        onClick={() => handleMainTabChange('create')}
                    >
                        Create Signature
                    </S.Tab>
                    <S.Tab
                        active={mainTab === 'my-signatures'}
                        onClick={() => handleMainTabChange('my-signatures')}
                    >
                        My Signatures ({signatures.length})
                    </S.Tab>
                </S.TabContainer>

                <TabPanel activeTab={mainTab} tabId="create">
                    {isLoading && signatures.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : !canCreate ? (
                        <Alert severity="info">
                            You have reached the maximum limit of 10 signatures. Delete an existing
                            signature to create a new one.
                        </Alert>
                    ) : (
                        <Box>
                            <S.TabContainer>
                                <S.Tab
                                    active={createTab === 'type'}
                                    onClick={() => handleCreateTabChange('type')}
                                >
                                    Type
                                </S.Tab>
                                <S.Tab
                                    active={createTab === 'draw'}
                                    onClick={() => handleCreateTabChange('draw')}
                                >
                                    Draw
                                </S.Tab>
                                <S.Tab
                                    active={createTab === 'upload'}
                                    onClick={() => handleCreateTabChange('upload')}
                                >
                                    Upload
                                </S.Tab>
                            </S.TabContainer>

                            <TabPanel activeTab={createTab} tabId="type">
                                <TypedSignatureCreator onSuccess={handleSignatureCreated} />
                            </TabPanel>

                            <TabPanel activeTab={createTab} tabId="draw">
                                <DrawnSignatureCreator onSuccess={handleSignatureCreated} />
                            </TabPanel>

                            <TabPanel activeTab={createTab} tabId="upload">
                                <UploadSignatureCreator onSuccess={handleSignatureCreated} />
                            </TabPanel>
                        </Box>
                    )}
                </TabPanel>

                <TabPanel activeTab={mainTab} tabId="my-signatures">
                    {isLoading && signatures.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <SignatureList />
                    )}
                </TabPanel>
            </Paper>
        </Container>
    );
}
